import { useCallback, useMemo } from 'react';
import { Button, ButtonSizes, ButtonVariants, useFormApi, useFormState } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { EMPTY_LIST } from '@/lib/utils';
import { useTranslations } from '@/hooks';
import {
  MIN_TERMINALS,
  SITE_FIELDS,
  emptyTerminalValues,
  nextTerminalKey,
} from '../../constants/sites.constants';
import { TerminalModalCard } from './TerminalModalCard';
import styles from './TerminalModalFields.module.scss';

/**
 * Props:
 *   maxTerminals  how many cards this modal may hold — the site's remaining slots
 *   canAddMore    false in edit mode, which saves the one terminal it opened on
 */
export const TerminalModalFields = ({ maxTerminals, canAddMore }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const terminalKeys = values[SITE_FIELDS.terminalKeys] ?? EMPTY_LIST;

  const addTerminal = useCallback(() => {
    const terminalKey = nextTerminalKey(terminalKeys);
    Object.entries(emptyTerminalValues(terminalKey)).forEach(([name, value]) =>
      setValue(name, value),
    );
    setValue(SITE_FIELDS.terminalKeys, [...terminalKeys, terminalKey]);
  }, [terminalKeys, setValue]);

  const removeTerminal = useCallback(
    (terminalKey) =>
      setValue(
        SITE_FIELDS.terminalKeys,
        terminalKeys.filter((key) => key !== terminalKey),
      ),
    [terminalKeys, setValue],
  );

  const addIcon = useMemo(() => <Plus size={16} />, []);
  const isFull = terminalKeys.length >= maxTerminals;

  return (
    <div className={styles.fields}>
      {terminalKeys.map((terminalKey, index) => (
        <TerminalModalCard
          key={terminalKey}
          terminalKey={terminalKey}
          heading={t('SITES_TERMINAL_HEADING', { index: index + 1 })}
          canRemove={terminalKeys.length > MIN_TERMINALS}
          onRemove={removeTerminal}
        />
      ))}

      {canAddMore && (
        <Button
          fullWidth
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          disabled={isFull}
          onClick={addTerminal}
        >
          {t('SITES_ADD_MORE_TERMINALS')}
        </Button>
      )}
    </div>
  );
};
